import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Award, Bell, User, Heart, MessageSquare, TrendingUp, ThumbsUp, CheckCircle, Loader2 } from 'lucide-react';

// --- MOCK API DATA ---
const mockEngagementData = {
  announcements: [
    { id: 1, title: 'New Remote Work Policy Q4 2025', date: 'Sept 26', icon: Bell, type: 'Policy Update' },
    { id: 2, title: 'Annual Company BBQ Date Confirmed!', date: 'Sept 20', icon: Heart, type: 'Social Event' },
  ],
  recognition: [
    { id: 101, employee: 'Sarah Connor', award: 'Innovation Award', month: 'September', recognizedBy: 'Management' },
    { id: 102, employee: 'John Doe', award: 'Top Performer', month: 'August', recognizedBy: 'Team Lead' },
  ],
  poll: {
    question: 'How do you prefer to receive internal updates?',
    options: [
      { id: 1, text: 'Dashboard Notifications', votes: 45 },
      { id: 2, text: 'Weekly Email Newsletter', votes: 30 },
      { id: 3, text: 'Team Meetings', votes: 25 },
    ],
    totalVotes: 100,
    isVoted: false,
  },
};

// --- API MOCK FUNCTIONS ---
const fetchEngagementData = async () => new Promise(resolve => {
    setTimeout(() => resolve({ data: mockEngagementData }), 900);
});

const submitPollVote = async (optionId) => new Promise(resolve => {
    setTimeout(() => {
        console.log("Vote submitted for option:", optionId);
        resolve({ success: true });
    }, 500);
});

// --- FRAMER MOTION VARIANTS ---
const pageVariants = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

const cardContainer = {
  visible: { transition: { staggerChildren: 0.1 } },
};

const cardItem = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100 } },
};


// --- UTILITY COMPONENT: AnnouncementCard ---
const AnnouncementCard = ({ item }) => {
  const Icon = item.icon;
  const color = item.type === 'Policy Update' ? 'text-blue-500' : 'text-pink-500';

  return (
    <motion.div variants={cardItem} whileHover={{ x: 5 }} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-indigo-400">
      <Icon size={20} className={color} />
      <div className="flex-grow">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{item.title}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{item.date} | {item.type}</p>
      </div>
      <motion.button whileTap={{ scale: 0.9 }} className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400" title="View Details">
        <MessageSquare size={16} />
      </motion.button>
    </motion.div>
  );
};


// --- UTILITY COMPONENT: RecognitionCard ---
const RecognitionCard = ({ item }) => (
    <motion.div 
        variants={cardItem}
        whileHover={{ scale: 1.05, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)' }}
        className="p-5 bg-yellow-50 dark:bg-yellow-900/40 rounded-xl shadow-md flex flex-col items-center text-center border-t-4 border-yellow-500"
    >
        <Award size={36} className="text-yellow-600 dark:text-yellow-400 mb-2" />
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{item.employee}</h3>
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">{item.award}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Recognized by: {item.recognizedBy}</p>
    </motion.div>
);


// --- UTILITY COMPONENT: QuickPoll ---
const QuickPoll = ({ pollData, updatePoll }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVote = async () => {
    if (!selectedOption) return;
    setIsSubmitting(true);
    await submitPollVote(selectedOption);

    // Update state to show results
    const updatedOptions = pollData.options.map(opt => 
        opt.id === selectedOption ? { ...opt, votes: opt.votes + 1 } : opt
    );
    const updatedPoll = { 
        ...pollData, 
        options: updatedOptions, 
        totalVotes: pollData.totalVotes + 1,
        isVoted: true 
    };
    updatePoll(updatedPoll);
    setIsSubmitting(false);
  };

  const isVoted = pollData.isVoted;
  const isButtonDisabled = isSubmitting || !selectedOption || isVoted;

  return (
    <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-t-4 border-green-500 flex flex-col h-full"
    >
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
        <TrendingUp size={24} className="mr-2 text-green-500" /> Quick Poll
      </h2>
      <p className="text-lg font-semibold text-indigo-700 dark:text-indigo-400 mb-4">{pollData.question}</p>

      <div className="space-y-3 flex-grow">
        {pollData.options.map(option => (
          <motion.div 
            key={option.id}
            initial={false}
            animate={{ scale: isVoted ? 1 : (selectedOption === option.id ? 1.02 : 1) }}
            className="w-full"
          >
            {isVoted ? (
              // Display Results (Progress Bar)
              <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg p-2 overflow-hidden transition-all duration-300">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(option.votes / pollData.totalVotes) * 100}%` }}
                  transition={{ duration: 1 }}
                  className="absolute top-0 left-0 h-full bg-green-200 dark:bg-green-700/50"
                ></motion.div>
                <div className="relative flex justify-between items-center text-sm font-medium text-gray-800 dark:text-gray-100">
                  <span>{option.text}</span>
                  <span className="font-mono text-green-700 dark:text-green-300">{option.votes} Votes ({Math.round((option.votes / pollData.totalVotes) * 100)}%)</span>
                </div>
              </div>
            ) : (
              // Display Voting Options
              <button
                onClick={() => setSelectedOption(option.id)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center ${selectedOption === option.id ? 'border-green-500 bg-green-50 dark:bg-green-900/30' : 'border-gray-200 hover:border-indigo-400 dark:border-gray-700 dark:hover:border-indigo-500 dark:bg-gray-700'}`}
              >
                {selectedOption === option.id ? <CheckCircle size={18} className="mr-3 text-green-600" /> : <ThumbsUp size={18} className="mr-3 text-gray-500" />}
                <span className="text-gray-800 dark:text-gray-100">{option.text}</span>
              </button>
            )}
          </motion.div>
        ))}
      </div>
      
      {/* Footer / Action */}
      <div className="mt-6 border-t border-gray-100 dark:border-gray-700 pt-4">
        {isVoted ? (
            <p className="text-sm text-center text-gray-600 dark:text-gray-400 font-medium">Thank you for voting! Total Votes: {pollData.totalVotes}</p>
        ) : (
            <motion.button
                onClick={handleVote}
                whileHover={{ scale: 1.02, boxShadow: '0 4px 10px rgba(16, 185, 129, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                disabled={isButtonDisabled}
                className={`w-full flex justify-center items-center py-3 text-sm font-bold rounded-md shadow-sm text-white ${isButtonDisabled ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
                {isSubmitting ? (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                        <Loader2 size={20} className="mr-2" />
                    </motion.div>
                ) : (
                    <ThumbsUp size={20} className="mr-2" />
                )}
                {isSubmitting ? 'Casting Vote...' : 'Cast My Vote'}
            </motion.button>
        )}
      </div>
    </motion.div>
  );
};


// --- MAIN COMPONENT: Engagement ---
const Engagement = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchEngagementData().then(response => {
            setData(response.data);
            setLoading(false);
        });
    }, []);
    
    // Handler to update poll state after submission
    const handlePollUpdate = (updatedPoll) => {
        setData(prev => ({ ...prev, poll: updatedPoll }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 text-indigo-600">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                    <Heart size={48} />
                </motion.div>
                <p className="mt-4 text-xl font-medium ml-4">Loading Engagement Hub...</p>
            </div>
        );
    }

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            className="p-4 sm:p-8 md:p-12 min-h-screen bg-gradient-to-br from-indigo-50 to-green-100 dark:from-gray-950 dark:to-gray-800 transition-colors duration-500"
        >
            
            {/* Header */}
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="mb-10 border-b-4 border-green-500 pb-4"
            >
                <h1 className="text-4xl font-extrabold text-green-700 dark:text-green-400 tracking-tight flex items-center">
                    <Heart size={30} className="mr-3" /> Employee Engagement Hub
                </h1>
                <p className="mt-1 text-lg text-gray-600 dark:text-gray-400">
                    Stay informed, celebrate success, and have your voice heard.
                </p>
            </motion.header>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* 1. Quick Poll (Takes 1 or 2 columns based on screen size) */}
                <motion.div
                    className="lg:col-span-2"
                    variants={cardItem}
                    initial="initial"
                    animate="animate"
                >
                    <QuickPoll pollData={data.poll} updatePoll={handlePollUpdate} />
                </motion.div>


                {/* 2. Recognition & Awards (Takes 1 column) */}
                <motion.div 
                    className="lg:col-span-1 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl"
                    variants={cardItem}
                    initial="initial"
                    animate="animate"
                >
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center border-b pb-3">
                        <Award size={24} className="mr-2 text-yellow-500" /> Recent Recognition
                    </h2>
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={cardContainer}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4"
                    >
                        {data.recognition.map(item => (
                            <RecognitionCard key={item.id} item={item} />
                        ))}
                    </motion.div>
                </motion.div>
            </div>
            
            {/* 3. Announcements (Full Width Section) */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-10 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl"
            >
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center border-b pb-3">
                    <Bell size={24} className="mr-2 text-indigo-500" /> Latest Announcements
                </h2>
                <motion.div 
                    initial="hidden"
                    animate="visible"
                    variants={cardContainer}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                    {data.announcements.map(item => (
                        <AnnouncementCard key={item.id} item={item} />
                    ))}
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default Engagement;